// src/pages/ProductPage.tsx
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom'; // Import Link
// --- Import necessary hooks, types, components ---
import { fetchProductById, fetchCustomer, fetchMtPrice } from '../utils/api'; // Need fetchCustomer, fetchMtPrice
import { Product, Customer, displayPrice, useHandleAddToCart, Modifier, formatProductNameForUrl } from '../utils/types'; // Ensure Modifier is imported

import { useAuth } from '../context/AuthContext';
import { useCustomerContext } from '../context/customerContext';
import { hasRole } from '../utils/auth';
import { toast } from 'react-toastify';
import CustomerProfileModal from '../components/CustomerProfileModal';
import ModifierModal from '../components/ModifierModal'; // Import ModifierModal
import { Helmet } from 'react-helmet-async'; // For setting page title
import Header from '../components/Header'; // Import Header if needed on this page
import ProductDetails from '../components/ProductDetails'; // Import the new component
import { IconContext } from "react-icons";
import { FaShieldHeart, FaLink, FaFacebook, FaTelegram, FaLine, FaTwitter, FaWhatsapp } from "react-icons/fa6";
import { IoLogoWechat } from "react-icons/io5";

// Define a type for the price data specific to this page
interface ProductPagePriceData {
    price: number;
    role: string;
}

const ProductPage = () => {
    const { productId } = useParams<{ productId: string }>();
    const [product, setProduct] = useState<Product | null>(null);
    const [loadingProduct, setLoadingProduct] = useState(true);
    const [errorProduct, setErrorProduct] = useState<string | null>(null);

    // Use the hook for cart actions AND modifier handling
    const {
        handleAddToCart,
        showModifierModal,
        handleCloseModifierModal,
        modifiers,
        selectedProduct,
        handleModifierSelection,
        handleConfirmModifiers,
        selectedModifiers
    } = useHandleAddToCart();

    const imageRef = useRef<HTMLImageElement>(null); // Ref for potential animation

    // --- State for Customer Profile Modal ---
    const [showCustomerProfileModal, setShowCustomerProfileModal] = useState(false);

    // --- State for MT Price specific to this product/user ---
    const [mtPriceData, setMtPriceData] = useState<ProductPagePriceData | null>(null);
    const [loadingMtPrice, setLoadingMtPrice] = useState(false);
    const [errorMtPrice, setErrorMtPrice] = useState<string | null>(null);


    // --- Replicate effectiveUser and customer data fetching from Home.tsx ---
    const { user: loggedInUser } = useAuth();
    const { selectedSessionCustomer } = useCustomerContext();

    const effectiveUser = useMemo(() => {
        const isAdminOrStaff = hasRole(['super_admin', 'staff']);
        const isAdminActingAs = isAdminOrStaff && selectedSessionCustomer;

        if (isAdminActingAs) {
            const customerRole = selectedSessionCustomer?.role || 'dealer';
            return {
                id: selectedSessionCustomer.user_id,
                role: customerRole,
                isAuthenticated: true,
                customer_id: selectedSessionCustomer.id
            };
        } else {
            let loggedInUserRole: string | undefined;
            let loggedInCustomerId: number | undefined;
            try {
                const rolesPermissionsItem = localStorage.getItem('rolesPermissions');
                if (rolesPermissionsItem) {
                    const rolesPermissions = JSON.parse(rolesPermissionsItem);
                    loggedInUserRole = rolesPermissions?.roles?.[0];
                }
                const storedCustomerId = localStorage.getItem('customer');
                if (storedCustomerId) {
                    const parsedId = JSON.parse(storedCustomerId);
                    if (typeof parsedId === 'number') {
                        loggedInCustomerId = parsedId;
                    } else if (typeof parsedId === 'object' && parsedId !== null && typeof parsedId.id === 'number') {
                        loggedInCustomerId = parsedId.id;
                    }
                }
            } catch (e) { console.error("Failed to parse roles/customer ID in ProductPage:", e); }
            return {
                id: loggedInUser?.id,
                role: loggedInUserRole,
                isAuthenticated: !!loggedInUser,
                customer_id: loggedInCustomerId
            };
        }
    }, [loggedInUser, selectedSessionCustomer]);

    const [effectiveCustomerData, setEffectiveCustomerData] = useState<Customer | null>(null);
    const [loadingCustomerData, setLoadingCustomerData] = useState(false);
    const [errorCustomerData, setErrorCustomerData] = useState<string | null>(null);

    useEffect(() => {
        const loadEffectiveCustomer = async () => {
            if (effectiveUser.isAuthenticated && effectiveUser.customer_id) {
                setLoadingCustomerData(true);
                setErrorCustomerData(null);
                try {
                    const customerData = await fetchCustomer(effectiveUser.customer_id);
                    setEffectiveCustomerData(customerData);
                } catch (err: any) {
                    console.error("Failed to load effective customer data:", err);
                    setErrorCustomerData(err.message || 'Failed to load customer details.');
                    setEffectiveCustomerData(null);
                } finally {
                    setLoadingCustomerData(false);
                }
            } else {
                setEffectiveCustomerData(null);
                setLoadingCustomerData(false);
                setErrorCustomerData(null);
            }
        };
        loadEffectiveCustomer();
    }, [effectiveUser.isAuthenticated, effectiveUser.customer_id]);
    // --- End replication ---

    // Fetch product details
    useEffect(() => {
        if (productId) {
            setLoadingProduct(true);
            setErrorProduct(null);
            setMtPriceData(null); // Reset price when product changes
            setErrorMtPrice(null);

            fetchProductById(parseInt(productId))
                .then(data => setProduct(data))
                .catch(err => setErrorProduct(err.message || 'Failed to load product.'))
                .finally(() => setLoadingProduct(false));
        } else {
            setErrorProduct("No Product ID provided.");
            setLoadingProduct(false);
        }
    }, [productId]);

    // --- Fetch MT Price for this specific product ---
    useEffect(() => {
        // Fetch only if product loaded, user is authenticated, and has ID/Role
        if (product && effectiveUser.isAuthenticated && effectiveUser.id && effectiveUser.role) {
            setLoadingMtPrice(true);
            setErrorMtPrice(null);
            fetchMtPrice(product.id, effectiveUser.id, effectiveUser.role)
                .then(priceData => {

                    
                    // Check if priceData exists and has the product ID as a key
                    if (priceData && typeof priceData === 'object' && product.id in priceData ) {
                        console.log('priceData : ', productId, priceData[product.id].price, effectiveUser.role);
                        setMtPriceData({ price: priceData[product.id].price, role: effectiveUser.role! });
                    } else {
                        // Handle case where price might not be found for this user/role or structure is different
                        console.warn(`MT Price not found or invalid format for product ${product.id}`, priceData);
                        setMtPriceData(null);
                    }
                })
                .catch(err => {
                    console.error("Failed to load MT price for product page:", err);
                    setErrorMtPrice(err.message || 'Failed to load specific price.');
                    setMtPriceData(null);
                })
                .finally(() => setLoadingMtPrice(false));
        } else {
            // Clear price if conditions not met
            setMtPriceData(null);
            setLoadingMtPrice(false);
            setErrorMtPrice(null);
        }
    }, [product, effectiveUser.isAuthenticated, effectiveUser.id, effectiveUser.role, productId]); // Dependencies


    // --- Add to Cart Handler specific to this page ---
    const handlePageAddToCartClick = () => {
        if (!product) return; // Should not happen if button is enabled

        // 1. Check Authentication
        if (!effectiveUser.isAuthenticated) {
            toast.error("Please log in to add items.");
            return;
        }

        // 2. Check loading state for customer data
        if (loadingCustomerData) {
            toast.info("Checking customer details...");
            return;
        }

        // 3. Check customer data and profile completeness
        if (!effectiveCustomerData) {
            toast.error("Could not verify customer details. Please try again or contact support.");
            console.error("Page Add to cart check failed: effectiveCustomerData is null");
            return;
        }
        const hasPhone = effectiveCustomerData.phone && String(effectiveCustomerData.phone).trim() !== '';
        const hasEmail = effectiveCustomerData.email && String(effectiveCustomerData.email).trim() !== '';

        if (!hasPhone || !hasEmail) {
            // 4. Show profile modal
            toast.warn("Please complete your profile (Phone and Email) before adding items.");
            setShowCustomerProfileModal(true);
        } else {
            // 5. Proceed with adding to cart using the hook
            // Use the fetched MTPrice or fallback to SRP
            const priceToAdd = mtPriceData?.price ?? product.product_value;
            handleAddToCart(product, imageRef, priceToAdd, effectiveUser.id, effectiveUser.role);
        }
    };

    // --- Handler to close the Customer Profile Modal ---
    const handleCustomerProfileModalClose = () => {
        setShowCustomerProfileModal(false);
    };

    // --- Render Logic ---

    // --- Product Loading Skeleton ---
    if (loadingProduct) {
        return (
            <>
                <Helmet><title>Loading Product...</title></Helmet>
                <div className="container mx-auto p-4 animate-pulse">
                    <div className="mb-4 h-6 bg-gray-200 rounded w-1/4"></div> {/* Back link placeholder */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Image Skeleton */}
                        <div>
                            <div className="w-full h-96 bg-gray-200 rounded shadow-md"></div>
                        </div>
                        {/* Details Skeleton */}
                        <div className="space-y-4">
                            <div className="h-8 bg-gray-200 rounded w-3/4"></div> {/* Title */}
                            <div className="h-4 bg-gray-200 rounded w-1/4"></div> {/* SKU */}
                            <div className="h-4 bg-gray-200 rounded w-1/3"></div> {/* Category/Brand */}
                            <div className="h-4 bg-gray-200 rounded w-1/3"></div> {/* Category/Brand */}

                            {/* Price Skeleton */}
                            <div className="my-5 p-3 bg-gray-100 rounded space-y-2">
                                <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                                <div className="h-5 bg-gray-200 rounded w-1/3"></div>
                            </div>

                            {/* Description Skeleton */}
                            <div className="space-y-2">
                                <div className="h-5 bg-gray-200 rounded w-1/4"></div> {/* Desc Title */}
                                <div className="h-4 bg-gray-200 rounded w-full"></div>
                                <div className="h-4 bg-gray-200 rounded w-full"></div>
                                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                            </div>

                            {/* Button Skeleton */}
                            <div className="h-12 bg-gray-200 rounded w-full"></div>
                        </div>
                    </div>
                </div>
            </>
        );
    }
    // --- End Product Loading Skeleton ---


    if (errorProduct) return <div className="container mx-auto p-4 text-center text-red-500">Error: {errorProduct}</div>;
    if (!product) return <div className="container mx-auto p-4 text-center">Product not found.</div>;

    // Social share URLs with the current page link
    const shareText = encodeURIComponent(`Check out this product: ${product?.product_name}`);
    const productUrl = window.location.href;
    const productImage = product?.media && product.media.length > 0 ? product.media[0].original_url : '../images/df.jpg';
    const productNameNormal = product?.product_name || 'Product';
    const productDescription = product?.product_desc || 'No description available';
    const productUrlName = encodeURIComponent(productUrl);
    const productUrlLink = product ? `${window.location.origin}/product/${product.id}/${formatProductNameForUrl(product.product_name)}` : '';

    console.log('mtPriceData : ', mtPriceData);

    return (
        <>
            {/* SEO and Open Graph meta tags using react-helmet */}
            <Helmet>
                <title>{productNameNormal} | u8.my</title>
                <meta name="description" content={productDescription} />
                <meta property="og:title" content={productNameNormal} />
                <meta property="og:description" content={productDescription} />
                <meta property="og:image" content={productImage} />
                <meta property="og:url" content={productUrl} />
                <meta property="og:type" content="product" />
                <meta property="og:site_name" content="u8.my" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={productNameNormal} />
                <meta name="twitter:description" content={productDescription} />
                <meta name="twitter:image" content={productImage} />
            </Helmet>

            {/* Render Header if you want it on this page */}
            {/* <Header /> */}
            <div className="container mx-auto p-4">
                {/* Breadcrumbs or Back Link */}
                <div className="mb-4">
                    <Link to="/" className="text-blue-600 hover:underline">&larr; Back to Products</Link>
                </div>

                {/* Product Details Display */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Image Column */}
                    <div ref={imageRef}>
                        <img
                            src={product.media.length > 0 ? product.media[0].original_url : 'placeholder.jpg'}
                            alt={product.product_name}
                            className="w-full h-auto object-contain rounded shadow-md"
                        />
                        {/* Add gallery/thumbnails if multiple images */}
                    </div>

                    {/* Details Column */}
                    <div>
                        <h1 className="text-3xl font-bold mb-3">{product.product_name}</h1>
                        <p className="text-md text-gray-500 mb-4">SKU: {product.product_code}</p>
                        {product.cat_name && <p className="text-sm text-gray-600 mb-1">Category: {product.cat_name}</p>}
                        {product.brand_name && <p className="text-sm text-gray-600 mb-4">Brand: {product.brand_name}</p>}


                        {/* Price Display */}
                        <div className="price-div  mb-5 p-3 bg-gray-100 rounded min-h-[6rem]"> {/* Added min-height */}
                            {/* --- Price Skeleton --- */}
                            {loadingMtPrice && effectiveUser.isAuthenticated && (
                                <div className="animate-pulse space-y-2">
                                    <div className="h-6 bg-gray-200 rounded w-1/2"></div> {/* Your Price placeholder */}
                                    <div className="h-5 bg-gray-200 rounded w-1/3"></div> {/* SRP placeholder */}
                                </div>
                            )}
                            {/* --- End Price Skeleton --- */}

                            {/* --- Actual Price (when not loading) --- */}
                            {!loadingMtPrice && (
                                <>
                                    {errorMtPrice && <p className="text-sm text-red-500">{errorMtPrice}</p>}
                                    {effectiveUser.isAuthenticated && mtPriceData && (
                                        <p className="text-xl text-green-700 font-semibold mb-1">
                                            Your VIP Price: <span className='font-bold'>{displayPrice(mtPriceData.price)}</span>
                                        </p>
                                    )}
                                    <p className={`text-lg text-gray-800 ${mtPriceData ? 'line-through text-sm' : ''}`}>
                                        SRP: <span className='font-semibold'>{displayPrice(product.product_value)}</span>
                                    </p>
                                </>
                            )}
                             {/* --- End Actual Price --- */}
                        </div>

                        {/* Description */}
                        <div className="prose max-w-none mb-6"> {/* Using prose for better text formatting */}
                            <h3 className="text-lg font-semibold mb-2">Description</h3>
                            <p>{product.product_desc || 'No description available.'}</p>
                        </div>

                        <div className='my-4'>
                            {/* Warranty (if available) */}
                            {product.warranty && (
                                <p className="text-sm md:text-md text-gray-600 mb-4">Warranty: {product.warranty}</p>
                            )}

                            {/* Stock Status */}
                            <p className={`text-sm md:text-md font-medium mb-4 ${product.stock && product.stock > 0 ? 'text-green-600' : 'text-orange-600'}`}>
                                {product.stock && product.stock > 0 ? `In Stock` : 'Back Order'}
                            </p>
                            {/* (${product.stock} available) */}

                            {/* Add to Cart Button */}
                            {effectiveUser.isAuthenticated && (
                                <button
                                    onClick={handlePageAddToCartClick}
                                    disabled={loadingCustomerData || loadingMtPrice} // Disable while loading customer or price
                                    className={`w-full px-6 py-3 bg-blue-600 text-white rounded hover:bg-blue-700 transition duration-150 text-lg font-semibold ${loadingCustomerData || loadingMtPrice ? 'opacity-50 cursor-wait' : ''}`}
                                >
                                    {loadingCustomerData ? 'Checking Details...' : loadingMtPrice ? 'Getting Price...' : 'Add to Cart'}
                                </button>
                            )}
                            {!effectiveUser.isAuthenticated && (
                                <p className="text-center mt-4">
                                    <Link to="/login" className="text-blue-600 hover:underline font-semibold">Log in</Link> to purchase.
                                </p>
                            )}
                            {errorCustomerData && <p className="text-sm text-red-500 mt-2">{errorCustomerData}</p>}
                        </div>

                        {/* Social Share Icons */}
                        <div className="social-share flex justify-center mt-0 space-x-4">
                            <a href={`https://www.facebook.com/sharer/sharer.php?u=${productUrlName}`} target="_blank" rel="noopener noreferrer" className='mr-2'>
                                <IconContext.Provider value={{ size: "1.6em", color: "#1877F2" }}>
                                    <FaFacebook />
                                </IconContext.Provider>
                            </a>
                            <a href={`https://social-plugins.line.me/lineit/share?url=${productUrlName}`} target="_blank" rel="noopener noreferrer" className='mr-2'>
                                <IconContext.Provider value={{ size: "1.6em", color: "#00C300" }}>
                                    <FaLine />
                                </IconContext.Provider>
                            </a>
                            <a href={`https://t.me/share/url?url=${productUrlName}`} target="_blank" rel="noopener noreferrer" className='mr-2'>
                                <IconContext.Provider value={{ size: "1.6em", color: "#0088cc" }}>
                                    <FaTelegram />
                                </IconContext.Provider>
                            </a>
                            <a href={`weixin://dl/officialaccounts?scene=108&need_open_webview=0&url=${productUrlName}`} target="_blank" rel="noopener noreferrer" className='mr-2'>
                                <IconContext.Provider value={{ size: "1.6em", color: "#1AAD19" }}>
                                    <IoLogoWechat />
                                </IconContext.Provider>
                            </a>
                            <a
                                href={`https://twitter.com/intent/tweet?url=${productUrlName}}&text=${shareText}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Share on Twitter"
                                className="text-blue-400"
                            >
                                <FaTwitter size={28} />
                            </a>
                            <a
                                href={`https://api.whatsapp.com/send?text=${shareText}%20${productUrlName}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Share on WhatsApp"
                                className="text-green-500"
                            >
                                <FaWhatsapp size={28} />
                            </a>
                        </div>

                        {/* Details Section (if available) */}
                        {product.details && (
                            <div className="prose max-w-none mb-6">
                                <h3 className="text-lg font-semibold mb-2">Details</h3>
                                <ProductDetails product={product} /> {/* Use your new component here */}
                            </div>
                        )}
                    </div>
                </div>

                {/* --- Render Customer Profile Modal Conditionally --- */}
                {showCustomerProfileModal && effectiveCustomerData && (
                    <CustomerProfileModal
                        initialCustomerData={effectiveCustomerData}
                        onClose={handleCustomerProfileModalClose}
                    />
                )}

                {/* --- Render Modifier Modal Conditionally (if triggered by handleAddToCart) --- */}
                {showModifierModal && selectedProduct && (
                    <ModifierModal
                        product={selectedProduct}
                        modifiers={modifiers}
                        onClose={handleCloseModifierModal}
                        onModifierSelection={handleModifierSelection}
                        onConfirm={handleConfirmModifiers}
                        selectedModifiers={selectedModifiers}
                    />
                )}
            </div>
        </>
    );
};

export default ProductPage;
